/**
 * h265_debug.ts — Dev-only structured logger + visual artifact probe for the
 * H.265 WASM pipeline. Everything in this module is gated by a global flag so
 * the production build remains silent.
 *
 * Enable via either:
 *   - localStorage.setItem('VGPS_H265_DEBUG', '1')
 *   - window.__VGPS_H265_DEBUG__ = true
 *
 * Levels of telemetry produced (all prefixed for easy grep / Playwright filter):
 *   [h265-debug]   periodic decoder feed / decode counters
 *   [h265-event]   one-shot lifecycle events (init, IDR seen, queue flush, ...)
 *   [h265-quality] visual artifact detector results (frame-stall, black, flat-block)
 *
 * Console errors emitted by the in-worker libavcodec (POC errors, CABAC) are
 * forwarded by the browser itself; we do NOT re-instrument the WASM worker.
 */

declare global {
  interface Window {
    __VGPS_H265_DEBUG__?: boolean;
  }
}

export function isH265DebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.__VGPS_H265_DEBUG__) return true;
  try {
    return window.localStorage?.getItem('VGPS_H265_DEBUG') === '1';
  } catch {
    return false;
  }
}

export interface FeedStatsSnapshot {
  feedTotal: number;
  feedIdr: number;
  feedP: number;
  feedBytes: number;
  workerOutMessages: number;
  workerInMessages: number;
  workerInRender: number;
  lastIdrAgoMs: number | null;
  lastFeedAgoMs: number | null;
  meanIdrIntervalMs: number | null;
}

export class H265DebugLogger {
  private enabled = false;
  private startTs = 0;

  private feedTotal = 0;
  private feedIdr = 0;
  private feedP = 0;
  private feedBytes = 0;

  private workerOut = 0;
  private workerIn = 0;
  private workerInRender = 0;

  private lastIdrTs: number | null = null;
  private lastFeedTs: number | null = null;
  private idrIntervals: number[] = [];

  private periodicTimer: number | null = null;
  private prevSnapshot: FeedStatsSnapshot | null = null;

  constructor(private readonly tag: string = 'h265-debug') {
    this.enabled = isH265DebugEnabled();
  }

  start(): void {
    if (!this.enabled) return;
    this.startTs = performance.now();
    this.event('logger started', { startTs: this.startTs });
    if (this.periodicTimer == null) {
      this.periodicTimer = window.setInterval(() => this._emitPeriodic(), 1000);
    }
  }

  stop(): void {
    if (this.periodicTimer != null) {
      clearInterval(this.periodicTimer);
      this.periodicTimer = null;
    }
  }

  /** Mark a decoder feed (called from WasmDecoder.feedNalu). */
  noteFeed(isKey: boolean, bytes: number): void {
    if (!this.enabled) return;
    const now = performance.now();
    this.feedTotal++;
    this.feedBytes += bytes;
    if (isKey) {
      this.feedIdr++;
      if (this.lastIdrTs != null) {
        const dt = now - this.lastIdrTs;
        this.idrIntervals.push(dt);
        if (this.idrIntervals.length > 30) this.idrIntervals.shift();
      }
      this.lastIdrTs = now;
    } else {
      this.feedP++;
    }
    this.lastFeedTs = now;
  }

  noteWorkerOut(): void {
    if (!this.enabled) return;
    this.workerOut++;
  }

  noteWorkerIn(type: string): void {
    if (!this.enabled) return;
    this.workerIn++;
    if (type === 'video_render_callback' || type === 'render') this.workerInRender++;
  }

  /** One-shot lifecycle event. Always logged when enabled (low frequency). */
  event(message: string, data?: Record<string, unknown>): void {
    if (!this.enabled) return;
    if (data) {
      // eslint-disable-next-line no-console
      console.info(`[h265-event] ${message}`, data);
    } else {
      // eslint-disable-next-line no-console
      console.info(`[h265-event] ${message}`);
    }
  }

  snapshot(): FeedStatsSnapshot {
    const now = performance.now();
    const meanIdrIntervalMs = this.idrIntervals.length
      ? this.idrIntervals.reduce((a, b) => a + b, 0) / this.idrIntervals.length
      : null;
    return {
      feedTotal: this.feedTotal,
      feedIdr: this.feedIdr,
      feedP: this.feedP,
      feedBytes: this.feedBytes,
      workerOutMessages: this.workerOut,
      workerInMessages: this.workerIn,
      workerInRender: this.workerInRender,
      lastIdrAgoMs: this.lastIdrTs == null ? null : now - this.lastIdrTs,
      lastFeedAgoMs: this.lastFeedTs == null ? null : now - this.lastFeedTs,
      meanIdrIntervalMs,
    };
  }

  private _emitPeriodic(): void {
    const snap = this.snapshot();
    const prev = this.prevSnapshot;
    const dFeed = prev ? snap.feedTotal - prev.feedTotal : snap.feedTotal;
    const dRender = prev ? snap.workerInRender - prev.workerInRender : snap.workerInRender;
    const dIdr = prev ? snap.feedIdr - prev.feedIdr : snap.feedIdr;
    this.prevSnapshot = snap;
    // eslint-disable-next-line no-console
    console.info(
      `[${this.tag}] 1s: feed=${dFeed} (idr=${dIdr}) rendered=${dRender}` +
        ` totalFeed=${snap.feedTotal} totalRender=${snap.workerInRender}` +
        ` lastIdrAgo=${snap.lastIdrAgoMs?.toFixed(0) ?? 'n/a'}ms` +
        ` meanIdrGap=${snap.meanIdrIntervalMs?.toFixed(0) ?? 'n/a'}ms`,
    );
    // Suspicious patterns — surface as warnings so they stand out in logs.
    if (dFeed > 0 && dRender === 0) {
      // eslint-disable-next-line no-console
      console.warn('[h265-debug] decoder STARVED: frames fed but none rendered in last 1s');
    }
    if (snap.lastIdrAgoMs != null && snap.lastIdrAgoMs > 3000) {
      // eslint-disable-next-line no-console
      console.warn(`[h265-debug] no IDR for ${snap.lastIdrAgoMs.toFixed(0)}ms (gop too long?)`);
    }
  }
}

// ── Visual quality probe ────────────────────────────────────────────────────

export interface VisualProbeOptions {
  /** Sampling interval in ms. */
  intervalMs?: number;
  /** Downscale size used for sampling (NxN). Default 32. */
  sampleSize?: number;
  /** Frames to compare for frame-stall detection. Default 6. */
  stallWindow?: number;
}

interface VisualSample {
  ts: number;
  hash: number;
  meanLuma: number;
  meanR: number;
  meanG: number;
  meanB: number;
  flatBlockRatio: number;
  /** Maximum absolute deviation between any two channel means (R/G/B). */
  maxChannelImbalance: number;
}

/**
 * Periodically samples a 2D canvas to detect visible artifacts even when the
 * decoder reports no errors. Designed for the `canvas2d` relay used by
 * HybridPlayerAdapter (already updated continuously via drawImage). Emits
 * `[h265-quality]` console events so external test harnesses can correlate
 * with screenshots.
 */
export class VisualArtifactProbe {
  private timer: number | null = null;
  private samples: VisualSample[] = [];
  private sampleCanvas: HTMLCanvasElement | null = null;
  private sampleCtx: CanvasRenderingContext2D | null = null;
  private intervalMs: number;
  private sampleSize: number;
  private stallWindow: number;
  private enabled = false;
  private firstSampleSeen = false;
  /** Gate detection until we have seen at least one non-black sample, so we
   * don't spam "black frame" warnings before the decoder has rendered anything. */
  private firstActiveFrameSeen = false;
  private startedAt = 0;
  /** State machine for each detector — only log the rising edge + summary. */
  private artifactState = {
    black: { active: false, since: 0, count: 0 },
    flat: { active: false, since: 0, count: 0 },
    color: { active: false, since: 0, count: 0 },
    stall: { active: false, since: 0, count: 0 },
  };

  constructor(
    private readonly source: HTMLCanvasElement,
    opts: VisualProbeOptions = {},
  ) {
    this.intervalMs = opts.intervalMs ?? 250;
    this.sampleSize = opts.sampleSize ?? 32;
    this.stallWindow = opts.stallWindow ?? 6;
    this.enabled = isH265DebugEnabled();
  }

  start(): void {
    if (!this.enabled || this.timer != null) return;
    const c = document.createElement('canvas');
    c.width = this.sampleSize;
    c.height = this.sampleSize;
    this.sampleCanvas = c;
    this.sampleCtx = c.getContext('2d', { willReadFrequently: true });
    if (!this.sampleCtx) return;
    this.startedAt = performance.now();
    this.timer = window.setInterval(() => this._tick(), this.intervalMs);
    // eslint-disable-next-line no-console
    console.info(
      `[h265-event] visual probe started size=${this.sampleSize} interval=${this.intervalMs}ms`,
    );
  }

  stop(): void {
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private _tick(): void {
    const ctx = this.sampleCtx;
    if (!ctx || !this.source) return;
    if (!this.source.width || !this.source.height) return;
    try {
      ctx.drawImage(this.source, 0, 0, this.sampleSize, this.sampleSize);
    } catch {
      return;
    }
    let img: ImageData;
    try {
      img = ctx.getImageData(0, 0, this.sampleSize, this.sampleSize);
    } catch {
      return;
    }
    const sample = this._analyse(img);
    if (!this.firstSampleSeen) {
      this.firstSampleSeen = true;
      // eslint-disable-next-line no-console
      console.info('[h265-event] visual probe first sample', sample);
    }
    this.samples.push(sample);
    if (this.samples.length > 64) this.samples.shift();
    this._detect(sample);
  }

  private _analyse(img: ImageData): VisualSample {
    const d = img.data;
    let sumY = 0;
    let sumR = 0;
    let sumG = 0;
    let sumB = 0;
    let hash = 2166136261 >>> 0; // FNV-1a 32-bit init
    // 4×4 block flat ratio: count blocks where max-min of luma < 4.
    const N = this.sampleSize;
    const block = 4;
    let totalBlocks = 0;
    let flatBlocks = 0;
    const luma = new Uint8Array(N * N);
    for (let i = 0, p = 0; p < d.length; p += 4, i++) {
      const r = d[p];
      const g = d[p + 1];
      const b = d[p + 2];
      const y = (r * 0.299 + g * 0.587 + b * 0.114) | 0;
      luma[i] = y;
      sumY += y;
      sumR += r;
      sumG += g;
      sumB += b;
      hash ^= y;
      hash = Math.imul(hash, 16777619) >>> 0;
    }
    const px = N * N;
    const meanLuma = sumY / px;
    const meanR = sumR / px;
    const meanG = sumG / px;
    const meanB = sumB / px;
    const maxChannelImbalance = Math.max(
      Math.abs(meanR - meanG),
      Math.abs(meanG - meanB),
      Math.abs(meanR - meanB),
    );
    for (let by = 0; by < N; by += block) {
      for (let bx = 0; bx < N; bx += block) {
        let mn = 255;
        let mx = 0;
        for (let yy = 0; yy < block; yy++) {
          for (let xx = 0; xx < block; xx++) {
            const v = luma[(by + yy) * N + (bx + xx)];
            if (v < mn) mn = v;
            if (v > mx) mx = v;
          }
        }
        totalBlocks++;
        if (mx - mn < 4) flatBlocks++;
      }
    }
    return {
      ts: performance.now(),
      hash,
      meanLuma,
      meanR,
      meanG,
      meanB,
      flatBlockRatio: flatBlocks / totalBlocks,
      maxChannelImbalance,
    };
  }

  private _detect(sample: VisualSample): void {
    // Suppress detection until at least one frame with real content has been
    // seen. Otherwise the empty 2D canvas at startup yields constant black-frame
    // and frame-stall warnings before the decoder has produced any output.
    if (!this.firstActiveFrameSeen) {
      if (sample.meanLuma >= 4) {
        this.firstActiveFrameSeen = true;
        const elapsed = (sample.ts - this.startedAt).toFixed(0);
        // eslint-disable-next-line no-console
        console.info(
          `[h265-event] first non-black sample after ${elapsed}ms meanLuma=${sample.meanLuma.toFixed(1)}`,
        );
      } else {
        // Still waiting for first real frame; emit one warning if it takes too long.
        const waited = sample.ts - this.startedAt;
        if (waited > 5000 && waited < 5000 + this.intervalMs * 1.5) {
          // eslint-disable-next-line no-console
          console.warn(
            `[h265-quality] no rendered frame after 5s (canvas still black) — decoder may be stuck`,
          );
        }
        return;
      }
    }
    // Detect each artifact category as a boolean condition; the state machine
    // below converts continuous signals into rising-edge / cleared events so
    // the log is readable even when corruption persists for many seconds.
    const isBlack = sample.meanLuma < 4;
    const isFlat = sample.flatBlockRatio > 0.85 && sample.meanLuma > 4;
    const isColor = sample.maxChannelImbalance > 60;
    let stallDur = 0;
    let isStall = false;
    const w = this.stallWindow;
    if (this.samples.length >= w) {
      const last = this.samples.slice(-w);
      if (last.every((s) => s.hash === sample.hash)) {
        isStall = true;
        stallDur = sample.ts - last[0].ts;
      }
    }
    this._stateLog('black', isBlack, sample.ts, () =>
      `[h265-quality] black frame START meanLuma=${sample.meanLuma.toFixed(1)}`);
    this._stateLog('flat', isFlat, sample.ts, () =>
      `[h265-quality] flat-block START ratio=${sample.flatBlockRatio.toFixed(2)} meanLuma=${sample.meanLuma.toFixed(1)}`);
    this._stateLog('color', isColor, sample.ts, () =>
      `[h265-quality] color-imbalance START R=${sample.meanR.toFixed(0)} G=${sample.meanG.toFixed(0)} B=${sample.meanB.toFixed(0)} maxDiff=${sample.maxChannelImbalance.toFixed(0)}`);
    this._stateLog('stall', isStall, sample.ts, () =>
      `[h265-quality] frame-stall START hash=${sample.hash} (${stallDur.toFixed(0)}ms identical)`);
  }

  /**
   * Edge-triggered logger for a continuous boolean condition. Emits one START
   * event when the condition becomes true and one END event (with duration +
   * sample count) when it becomes false. Suppresses spam while in-state.
   */
  private _stateLog(
    key: 'black' | 'flat' | 'color' | 'stall',
    active: boolean,
    ts: number,
    startMsg: () => string,
  ): void {
    const s = this.artifactState[key];
    if (active) {
      if (!s.active) {
        s.active = true;
        s.since = ts;
        s.count = 1;
        // eslint-disable-next-line no-console
        console.warn(startMsg());
      } else {
        s.count++;
      }
    } else if (s.active) {
      const dur = (ts - s.since).toFixed(0);
      // eslint-disable-next-line no-console
      console.warn(`[h265-quality] ${key} END dur=${dur}ms samples=${s.count}`);
      s.active = false;
      s.count = 0;
    }
  }
}

// ── WebCodecs stats logger ──────────────────────────────────────────────────

export class WebCodecsDebugLogger {
  private enabled = false;
  private decodeAttempts = 0;
  private decoderErrors = 0;
  private recoveries = 0;
  private idrFed = 0;
  private periodicTimer: number | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private statsProvider: (() => any) | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private prevStats: any = null;

  constructor(private readonly tag: string = 'wc-debug') {
    this.enabled = isH265DebugEnabled();
  }

  start(): void {
    if (!this.enabled || this.periodicTimer != null) return;
    this.periodicTimer = window.setInterval(() => this._emit(), 1000);
  }

  stop(): void {
    if (this.periodicTimer != null) {
      clearInterval(this.periodicTimer);
      this.periodicTimer = null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setStatsProvider(provider: () => any): void {
    this.statsProvider = provider;
  }

  noteDecode(isKey: boolean): void {
    if (!this.enabled) return;
    this.decodeAttempts++;
    if (isKey) this.idrFed++;
  }

  noteError(): void {
    if (!this.enabled) return;
    this.decoderErrors++;
  }

  noteRecovery(): void {
    if (!this.enabled) return;
    this.recoveries++;
    this._postRecoveryWatch = 8; // log next 8 NALs to detect P-frame leakage
  }

  /** Call from decode() path — logs the first N NALs after each recovery. */
  notePostRecoveryNal(isKeyframe: boolean, naluFirstByte: number): void {
    if (!this.enabled || this._postRecoveryWatch <= 0) return;
    this._postRecoveryWatch--;
    const nalType = (naluFirstByte >> 1) & 0x3f;
    // eslint-disable-next-line no-console
    console.info(
      `[wc-debug] post-recovery NAL #${8 - this._postRecoveryWatch}` +
        ` nut=${nalType} isKeyframe=${isKeyframe}`,
    );
  }

  private _postRecoveryWatch = 0;

  event(message: string, data?: Record<string, unknown>): void {
    if (!this.enabled) return;
    if (data) {
      // eslint-disable-next-line no-console
      console.info(`[h265-event] ${message}`, data);
    } else {
      // eslint-disable-next-line no-console
      console.info(`[h265-event] ${message}`);
    }
  }

  private _emit(): void {
    const stats = this.statsProvider ? this.statsProvider() : null;
    const decoded = stats?.decodedFrames ?? 0;
    const dropped = stats?.droppedFrames ?? 0;
    const fps = stats?.framesPerSecond;
    const dDecoded = this.prevStats ? decoded - (this.prevStats.decodedFrames ?? 0) : decoded;
    const dDropped = this.prevStats ? dropped - (this.prevStats.droppedFrames ?? 0) : dropped;
    this.prevStats = stats ? { ...stats } : null;
    // eslint-disable-next-line no-console
    console.info(
      `[${this.tag}] 1s: decoded=${dDecoded} dropped=${dDropped} fps=${fps?.toFixed?.(1) ?? 'n/a'}` +
        ` totalDecoded=${decoded} totalDropped=${dropped} errors=${this.decoderErrors}` +
        ` recoveries=${this.recoveries} idrFed=${this.idrFed}`,
    );
  }
}
